import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Quote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ALBUMS } from "@/data/photos";

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const navigate = useNavigate();
  const portraitsAlbum = ALBUMS.find((a) => a.id === "portraits")!;

  return (
    /* @section: about */
    <section id="apropos" className="py-24 lg:py-32 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image side */}
          {/* @section: about-image */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[3/4]">
              <img
                src={portraitsAlbum.photos[1].url}
                alt="Mme Belhuguette Beko Bokau"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
            </div>

            {/* Floating quote card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="absolute -bottom-6 -right-4 lg:-right-8 bg-card border border-border rounded-2xl p-5 max-w-xs shadow-[0_8px_32px_oklch(0.28_0.09_240/0.15)]"
            >
              <Quote className="w-6 h-6 text-accent mb-2" />
              <p className="text-sm text-foreground/80 leading-relaxed italic">
                "Chaque geste de solidarité peut changer une vie."
              </p>
              <div className="mt-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">BB</div>
                <div>
                  <p className="text-xs font-semibold text-foreground">Mme Belhuguette Beko Bokau</p>
                  <p className="text-[10px] text-muted-foreground">Fondatrice, FGNB</p>
                </div>
              </div>
            </motion.div>

            {/* Mini album strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="absolute -top-4 -left-4 flex gap-2"
            >
              {portraitsAlbum.photos.slice(2, 5).map((p) => (
                <div key={p.id} className="w-14 h-14 rounded-xl overflow-hidden border-2 border-card shadow-md">
                  <img src={p.url} alt={p.caption} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </motion.div>

            <div className="absolute -bottom-2 -left-4 w-24 h-24 rounded-2xl border-2 border-accent/30 -z-10" />
          </motion.div>

          {/* Text side */}
          {/* @section: about-text */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            <div>
              <span className="inline-block text-accent text-sm font-semibold uppercase tracking-[0.15em] mb-3">
                À propos de la fondation
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Une vision née{" "}
                <span className="text-primary">d'une conviction</span>{" "}
                profonde
              </h2>
            </div>

            <p className="text-muted-foreground leading-relaxed text-base">
              Je suis <strong className="text-foreground">Madame Belhuguette Beko Bokau</strong>, entrepreneuse
              et femme d'affaires engagée pour le développement humain et la solidarité.
            </p>
            <p className="text-muted-foreground leading-relaxed text-base">
              Animée par une profonde conviction que chaque être humain mérite une chance de réussir
              et de vivre dans la dignité, j'ai créé la <strong className="text-foreground">Fondation Gath Naël Beko (FGNB)</strong>,
              une organisation humanitaire dont la mission est de transformer des vies à travers des actions concrètes.
            </p>

            <div className="grid grid-cols-2 gap-4 pt-2">
              {[
                { label: "Organisation humanitaire", sub: "Reconnue et engagée" },
                { label: "Actions concrètes", sub: "Sur le terrain" },
                { label: "Impact durable", sub: "Générations futures" },
                { label: "Transparence totale", sub: "Responsabilité absolue" },
              ].map(({ label, sub }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="mt-1 w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground">{sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { navigate("/fondatrice"); window.scrollTo({ top: 0 }); }}
                className="px-7 py-3.5 bg-primary text-primary-foreground font-semibold rounded-full hover:bg-primary/90 transition-all duration-200 shadow-[0_4px_20px_oklch(0.28_0.09_240/0.3)] hover:scale-[1.02] active:scale-[0.98]"
              >
                Découvrir la fondatrice
              </button>
              <button
                onClick={() => { navigate("/galerie"); window.scrollTo({ top: 0 }); }}
                className="px-7 py-3.5 border border-border text-foreground font-semibold rounded-full hover:border-accent/40 hover:text-accent transition-all duration-200"
              >
                Notre galerie
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
